// frontend/src/components/ROTeam/TeamBigFiveTab.js
import React, { useMemo, useState, useCallback } from 'react';
import {
  Brain, Eye, Loader2, Lock, ShieldCheck,
} from 'lucide-react';
import toast from '../../utils/toast';
import { formatDate } from '../../utils/dateUtils';
import { talentAssessmentsAPI } from '../../services/api';
import { useTeamBigFiveDirectory, useTeamBigFiveDepartmentAggregate } from '../../hooks/useTeamBigFive';
import DebouncedSearchInput from '../UI/DebouncedSearchInput';
import IndividualResultModal from '../../pages/admin/TalentInsights/IndividualResultModal';

/**
 * Match the viewed employee's department against the viewer's own headed
 * department(s) and fold in the shared `k_threshold` so
 * `IndividualResultModal`'s in-context radar has everything it needs in
 * one prop. Returns `null` when the employee's department isn't one the
 * viewer heads (e.g. an RO's direct report outside their own department,
 * or the department-aggregate request never fired) — the modal then
 * renders no overlay section at all, exactly like admin mode.
 */
const buildDepartmentAggregateProp = (departmentsAggregate, department, kThreshold) => {
  if (!department) return null;
  const entry = (departmentsAggregate || []).find((d) => d.department === department);
  if (!entry) return null;
  return { ...entry, k_threshold: kThreshold };
};

/**
 * My Team → Big Five tab.
 *
 * HOD / Deputy / RO mental model: open My Team, tap Big Five, find the
 * person, open their profile for a career conversation. Not buried under
 * attendance Individual.
 */
const TeamBigFiveTab = () => {
  const { data, isLoading, isError, error, refetch } = useTeamBigFiveDirectory();
  const [search, setSearch] = useState('');
  const [pendingStaffId, setPendingStaffId] = useState(null);
  const [modalTarget, setModalTarget] = useState(null);

  const employees = useMemo(() => data?.employees || [], [data]);

  // Department-average radar overlay is HOD/Deputy HOD only (see plan) —
  // don't fire the request at all for RO-only viewers.
  const isDepartmentHead = useMemo(
    () => employees.some((e) => e.viewer_relationship === 'hod' || e.viewer_relationship === 'deputy_hod'),
    [employees],
  );
  const { data: deptAggregateData } = useTeamBigFiveDepartmentAggregate({
    enabled: isDepartmentHead,
  });
  const kThreshold = deptAggregateData?.k_threshold;
  const departmentsAggregate = deptAggregateData?.departments || [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter((e) =>
      [e.display_name, e.staff_id, e.department, e.designation]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [employees, search]);

  const handleOpen = useCallback(async (emp) => {
    if (!emp?.has_result || pendingStaffId) return;
    setPendingStaffId(emp.staff_id);
    try {
      const response = await talentAssessmentsAPI.getTeamBigFiveIndividualResult(
        emp.staff_id,
        {},
        { expectedStatuses: [400] },
      );
      setModalTarget({
        staff_id: emp.staff_id,
        display_name: emp.display_name,
        department: emp.department,
        _preloadedResult: response.data,
      });
    } catch (err) {
      if (err.response?.status === 400 && err.response?.data?.code === 'justification_required') {
        setModalTarget({
          staff_id: emp.staff_id,
          display_name: emp.display_name,
          department: emp.department,
        });
        return;
      }
      if (err.response?.status === 404) {
        toast.info('No Big Five Profile on file for this person yet.');
        refetch();
        return;
      }
      if (err.response?.status === 403) {
        toast.error('You do not have access to this Big Five Profile.');
        return;
      }
      toast.error('Could not open Big Five Profile. Please try again.');
    } finally {
      setPendingStaffId(null);
    }
  }, [pendingStaffId, refetch]);

  if (isError && error?.response?.status === 403) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white p-12 text-center shadow-sm">
        <Lock className="w-10 h-10 text-gray-300 mx-auto" />
        <h2 className="mt-3 text-lg font-semibold text-gray-700">Access restricted</h2>
        <p className="mt-1 text-sm text-gray-500 max-w-md mx-auto">
          Big Five profiles are available to Heads of Department, Deputy HODs, and Reporting Officers for staff in their scope.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Intro */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
            <Brain className="w-5 h-5 text-blue-800" />
          </div>
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-gray-900">Big Five Profiles</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Open a team member&apos;s profile to prepare for career and coaching conversations.
              Every view is logged with your name and reason — they can see it on their access log.
            </p>
          </div>
        </div>
        <div className="mt-3 rounded-xl bg-blue-50 border border-blue-100 px-3 py-2.5 flex items-start gap-2">
          <ShieldCheck className="w-4 h-4 text-blue-800 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-blue-900 leading-relaxed">
            Use these profiles to guide development discussions — not as the sole basis for hiring or disciplinary decisions.
          </p>
        </div>
      </div>

      {/* Toolbar */}
      <DebouncedSearchInput
        value={search}
        onSearch={setSearch}
        placeholder="Search by name, ID, department"
        theme="employee"
        className="w-full sm:max-w-md"
      />

      {/* Body */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {isLoading && (
          <div className="py-16 flex items-center justify-center">
            <Loader2 className="w-8 h-8 text-blue-800 animate-spin" />
          </div>
        )}

        {isError && error?.response?.status !== 403 && (
          <div className="py-12 text-center text-sm text-gray-500">
            Could not load team Big Five profiles.
          </div>
        )}

        {!isLoading && !isError && filtered.length === 0 && (
          <div className="py-12 text-center px-4">
            <Brain className="w-10 h-10 text-gray-300 mx-auto" />
            <h3 className="mt-3 text-base font-semibold text-gray-900">
              {employees.length === 0 ? 'No team members in scope' : 'No matches'}
            </h3>
            <p className="mt-1 text-sm text-gray-500 max-w-md mx-auto">
              {employees.length === 0
                ? 'When you have direct reports or department staff, they will appear here.'
                : 'Try a different name, staff ID, or department.'}
            </p>
          </div>
        )}

        {!isLoading && !isError && filtered.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr className="text-left text-xs uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-3 font-semibold">Team member</th>
                  <th className="px-4 py-3 font-semibold hidden md:table-cell">Department</th>
                  <th className="px-4 py-3 font-semibold hidden lg:table-cell">Your access</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold text-right">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {filtered.map((emp) => {
                  const opening = pendingStaffId === emp.staff_id;
                  return (
                    <tr key={emp.staff_id} className="hover:bg-gray-50 transition">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">
                          {emp.display_name || '—'}
                        </div>
                        <div className="text-xs text-gray-500">{emp.staff_id}</div>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell text-gray-700">
                        {emp.department || '—'}
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <span className="inline-flex items-center rounded-full ring-1 ring-inset ring-blue-600/20 bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-800">
                          {emp.viewer_relationship_label || '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {emp.has_result ? (
                          <div>
                            <span className="inline-flex items-center rounded-full ring-1 ring-inset ring-emerald-600/20 bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
                              Profile ready
                            </span>
                            {emp.submitted_at && (
                              <div className="text-[11px] text-gray-400 mt-1">
                                {formatDate(emp.submitted_at)}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                            <Lock className="w-3.5 h-3.5" />
                            Not completed
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {emp.has_result ? (
                          <button
                            type="button"
                            onClick={() => handleOpen(emp)}
                            disabled={!!pendingStaffId}
                            className="icon-button"
                            title={`View Big Five for ${emp.display_name || emp.staff_id}`}
                            aria-label={`View Big Five for ${emp.display_name || emp.staff_id}`}
                          >
                            {opening ? (
                              <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                            ) : (
                              <Eye className="w-4 h-4" aria-hidden="true" />
                            )}
                          </button>
                        ) : (
                          <span className="text-xs text-gray-400 pr-2">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modalTarget && (
        <IndividualResultModal
          target={modalTarget}
          onClose={() => setModalTarget(null)}
          mode="team"
          departmentAggregate={buildDepartmentAggregateProp(
            departmentsAggregate, modalTarget.department, kThreshold,
          )}
        />
      )}
    </div>
  );
};

export default TeamBigFiveTab;
